"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.freezeRecordAttribute = void 0;
const index_js_1 = require("../../errors/index.js");
const attributeOptions_js_1 = require("../constants/attributeOptions.js");
const hasDefinedDefault_js_1 = require("../shared/hasDefinedDefault.js");
const validate_js_1 = require("../shared/validate.js");
const interface_js_1 = require("./interface.js");
/**
 * Freezes a warm `record` attribute
 *
 * @param keys Attribute keys
 * @param elements Attribute elements
 * @param state Attribute options
 * @param path Path of the instance in the related schema (string)
 * @return void
 */
const freezeRecordAttribute = (state, keys, elements, path) => {
    (0, validate_js_1.validateAttributeProperties)(state, path);
    const { required: keysRequired, hidden: keysHidden, key: keysKey, savedAs: keysSavedAs } = keys[attributeOptions_js_1.$state];
    if (keys[attributeOptions_js_1.$type] !== 'string') {
        throw new index_js_1.DynamoDBToolboxError('schema.recordAttribute.invalidKeys', {
            message: `Invalid record keys${path !== undefined ? ` at path '${path}'` : ''}: Record keys must be a string.`,
            path
        });
    }
    // Checking $key before $required as $key implies attribute is always $required
    if (keysKey !== false) {
        throw new index_js_1.DynamoDBToolboxError('schema.recordAttribute.keyKeys', {
            message: `Invalid record keys${path !== undefined ? ` at path '${path}'` : ''}: Record keys cannot be part of primary key.`,
            path
        });
    }
    if (keysRequired !== 'atLeastOnce') {
        throw new index_js_1.DynamoDBToolboxError('schema.recordAttribute.optionalKeys', {
            message: `Invalid record keys${path !== undefined ? ` at path '${path}'` : ''}: Record keys must be required.`,
            path
        });
    }
    if (keysHidden !== false) {
        throw new index_js_1.DynamoDBToolboxError('schema.recordAttribute.hiddenKeys', {
            message: `Invalid record keys${path !== undefined ? ` at path '${path}'` : ''}: Record keys cannot be hidden.`,
            path
        });
    }
    if (keysSavedAs !== undefined) {
        throw new index_js_1.DynamoDBToolboxError('schema.recordAttribute.savedAsKeys', {
            message: `Invalid record keys${path !== undefined ? ` at path '${path}'` : ''}: Record keys cannot be renamed (have savedAs option).`,
            path
        });
    }
    if ((0, hasDefinedDefault_js_1.hasDefinedDefault)(keys)) {
        throw new index_js_1.DynamoDBToolboxError('schema.recordAttribute.defaultedKeys', {
            message: `Invalid record keys${path !== undefined ? ` at path '${path}'` : ''}: Record keys cannot have default or linked values.`,
            path
        });
    }
    const { key: elementsKey, required: elementsRequired, hidden: elementsHidden, savedAs: elementsSavedAs } = elements[attributeOptions_js_1.$state];
    // Checking $key before $required as $key implies attribute is always $required
    if (elementsKey !== false) {
        throw new index_js_1.DynamoDBToolboxError('schema.recordAttribute.keyElements', {
            message: `Invalid record elements${path !== undefined ? ` at path '${path}'` : ''}: Record elements cannot be part of primary key.`,
            path
        });
    }
    if (elementsRequired !== 'atLeastOnce') {
        throw new index_js_1.DynamoDBToolboxError('schema.recordAttribute.optionalElements', {
            message: `Invalid record elements${path !== undefined ? ` at path '${path}'` : ''}: Record elements must be required.`,
            path
        });
    }
    if (elementsHidden !== false) {
        throw new index_js_1.DynamoDBToolboxError('schema.recordAttribute.hiddenElements', {
            message: `Invalid record elements${path !== undefined ? ` at path '${path}'` : ''}: Record elements cannot be hidden.`,
            path
        });
    }
    if (elementsSavedAs !== undefined) {
        throw new index_js_1.DynamoDBToolboxError('schema.recordAttribute.savedAsElements', {
            message: `Invalid record elements${path !== undefined ? ` at path '${path}'` : ''}: Record elements cannot be renamed (have savedAs option).`,
            path
        });
    }
    if ((0, hasDefinedDefault_js_1.hasDefinedDefault)(elements)) {
        throw new index_js_1.DynamoDBToolboxError('schema.recordAttribute.defaultedElements', {
            message: `Invalid record elements${path !== undefined ? ` at path '${path}'` : ''}: Records elements cannot have default or linked values.`,
            path
        });
    }
    const frozenKeys = keys.freeze(path && `${path} (KEY)`);
    const frozenElements = elements.freeze(`${path !== null && path !== void 0 ? path : ''}[string]`);
    return new interface_js_1.RecordAttribute_({
        path,
        keys: frozenKeys,
        elements: frozenElements,
        ...state
    });
};
exports.freezeRecordAttribute = freezeRecordAttribute;
